import { useManager } from 'dreamstate';
import { ElementType, useMemo } from 'react';

import { Flex, rem, Text } from '@vimeo/bokeh';
import { EllipsisV } from '@vimeo/bokeh-icons';
import { createInteractionDomName } from '@vimeo/cloud-live/libs/shared/utils/dom/identifying';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Id, Maybe } from '@vimeo/core/types/generic';
import { LiveErrorBoundary } from '@vimeo/live-core/components/error/boundary';
import { ChatSettingsButton } from '@vimeo/live-core/components/interaction/chat/ChatSettingsButton';
import { PollSettingsButton } from '@vimeo/live-core/components/interaction/polls/PollSettingsButton';
import { QnaSettingsButton } from '@vimeo/live-core/components/interaction/qna/QnaSettingsButton';
import { BlockingLoadingWrapper } from '@vimeo/live-core/components/loading';
import { useLiveGlobals } from '@vimeo/live-core/components/providers/globals';
import { IFirebaseContext, InteractionFirebaseManager } from '@vimeo/live-core/store/firebase/base';
import { T_CHAT, T_POLLS, T_QNA } from '@vimeo/live-core/translations';
import { ELiveErrorCode } from '@vimeo/live-core/types/error';
import {
  EChatType,
  EInteractionModule,
  IInteractionToolsItemDescriptor,
} from '@vimeo/live-core/types/interaction_tools';
import { IInteractionStoreInitialState } from '@vimeo/live-core/types/store';
import { LiveError } from '@vimeo/live-core/utils/error/LiveError';

import { ChatManage } from '../../chat/ChatManage';
import { ChatView } from '../../chat/ChatView';
import { PollManage } from '../../polls/manage/PollManage';
import { PollView } from '../../polls/view/PollView';
import { QnaManage } from '../../qna/QnaManage';
import { QnaView } from '../../qna/QnaView';

interface IInteractionContentProps extends IBaseDomElementProps {
  activeModule: Maybe<EInteractionModule>;
  isManagementAccessed: boolean;
  firebaseContext?: IFirebaseContext;
}

export function InteractionContent({
  id = createInteractionDomName('content'),
  activeModule,
  isManagementAccessed,
  // eslint-disable-next-line react-hooks/rules-of-hooks
  firebaseContext: { isInteractionReady } = useManager(InteractionFirebaseManager),
}: IInteractionContentProps) {
  const { initialState } = useLiveGlobals<IInteractionStoreInitialState>();

  const {
    id: itemId,
    header,
    headerMenu,
    Content,
  }: Partial<IInteractionToolsItemDescriptor> = useMemo(() => {
    switch (activeModule) {
      case EInteractionModule.CHAT:
        return {
          id: EInteractionModule.CHAT,
          header: T_CHAT,
          headerMenu: isManagementAccessed ? (
            <ChatSettingsButton buttonIcon={<EllipsisV />} />
          ) : undefined,
          Content: isManagementAccessed
            ? ChatManage
            : ({ id }: { id: string }) => <ChatView id={id} chatType={EChatType.PUBLIC} />,
        };

      case EInteractionModule.POLL:
        return {
          id: EInteractionModule.POLL,
          header: T_POLLS,
          headerMenu: isManagementAccessed ? (
            <PollSettingsButton buttonIcon={<EllipsisV />} />
          ) : undefined,
          Content: isManagementAccessed ? PollManage : PollView,
        };

      case EInteractionModule.QNA:
        return {
          id: EInteractionModule.QNA,
          header: T_QNA,
          headerMenu: isManagementAccessed ? (
            <QnaSettingsButton buttonIcon={<EllipsisV />} />
          ) : undefined,
          Content: isManagementAccessed ? QnaManage : QnaView,
        };

      default:
        throw new LiveError(`Unexpected activeModule provided: '${activeModule}'.`, {
          code: ELiveErrorCode.INVALID_PARAMETERS,
        });
    }
  }, [activeModule, isManagementAccessed]);

  const ItemContent: ElementType = Content as ElementType;

  return (
    <Flex
      id={id}
      height={'100%'}
      width={'100%'}
      maxWidth={rem(640)}
      flexDirection={'column'}
      flexGrow={1}
      flexBasis={0}
      overflow={'hidden'}
      backgroundColor={initialState?.interaction?.backgroundColor || 'background'}
    >
      {isInteractionReady ? (
        <>
          {isManagementAccessed ? (
            <Flex
              id={createInteractionDomName(id, 'content-header', itemId as Id)}
              justifyContent={'space-between'}
              alignItems={'center'}
              padding={rem(16)}
            >
              <Text
                id={createInteractionDomName(id, 'header')}
                variant={'heading-sm'}
                color={'text-primary'}
              >
                {header}
              </Text>

              {headerMenu ? headerMenu : null}
            </Flex>
          ) : null}

          <LiveErrorBoundary key={itemId} additionalInfo={itemId} component={'InteractionContent'}>
            <ItemContent id={createInteractionDomName(id, 'item-content', itemId as Id)} />
          </LiveErrorBoundary>
        </>
      ) : (
        <BlockingLoadingWrapper opacity={0.1} withAnimation />
      )}
    </Flex>
  );
}
